import { computed } from 'vue';
import { useAccount } from './useAccount';
import { useMapGetter } from './store';

export const FEATURE_FLAG_CACHE_TTL_MS = 60 * 1000;

const cache = new Map();

const cacheKey = ({ flag, accountId, userId }) =>
  [flag, accountId || 'no-account', userId || 'anonymous'].join(':');

export const clearFeatureFlagCache = () => cache.clear();

export const resolveFeatureFlagValue = (
  flag,
  {
    accountId,
    userId,
    defaultValue = false,
    evaluator,
    now = Date.now(),
  } = {}
) => {
  if (!flag) return Boolean(defaultValue);

  const key = cacheKey({ flag, accountId, userId });
  const cached = cache.get(key);
  if (cached && cached.expiresAt > now) return cached.value;

  const value =
    typeof evaluator === 'function'
      ? Boolean(evaluator(flag, { accountId, userId }))
      : Boolean(defaultValue);

  cache.set(key, { value, expiresAt: now + FEATURE_FLAG_CACHE_TTL_MS });
  return value;
};

export function useFeatureFlag(flag, { defaultValue = false } = {}) {
  const { accountId, isCloudFeatureEnabled } = useAccount();
  const currentUser = useMapGetter('getCurrentUser');

  const evaluator = targetFlag => isCloudFeatureEnabled(targetFlag);
  const isEnabled = computed(() =>
    resolveFeatureFlagValue(flag, {
      accountId: accountId.value,
      userId: currentUser.value?.id,
      defaultValue,
      evaluator,
    })
  );

  const getFeatureFlagEnabled = (
    targetFlag = flag,
    targetDefaultValue = defaultValue
  ) =>
    resolveFeatureFlagValue(targetFlag, {
      accountId: accountId.value,
      userId: currentUser.value?.id,
      defaultValue: targetDefaultValue,
      evaluator,
    });

  return {
    isEnabled,
    getFeatureFlagEnabled,
  };
}

export default useFeatureFlag;
